import { Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"

interface AiGeneratedNoticeProps {
  /**
   * caption: full text row shown below/near a full-size video (default).
   * badge: compact corner pill for grid thumbnails.
   */
  variant?: "caption" | "badge"
  className?: string
}

/**
 * Disclosure that a video was produced by generative AI.
 *
 * Satisfies the visible (사람이 인식할 수 있는) marking required by Korea's AI 기본법
 * 제31조 (투명성 확보 의무). The lucide icon uses `currentColor`, so passing a text
 * color via `className` (e.g. `text-white/70` over a dark background) recolors both the
 * icon and the label.
 */
export function AiGeneratedNotice({ variant = "caption", className }: AiGeneratedNoticeProps) {
  const { t } = useTranslation()

  if (variant === "badge") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[11px] font-medium leading-none text-white backdrop-blur-sm",
          className
        )}
      >
        <Sparkles className="h-2.5 w-2.5" />
        {t("common.aiGeneratedBadge")}
      </span>
    )
  }

  return (
    <p className={cn("flex items-center gap-1.5 text-xs text-slate-500", className)}>
      <Sparkles className="h-3.5 w-3.5 shrink-0" />
      {t("common.aiGeneratedNotice")}
    </p>
  )
}
